import {
  InputHTMLAttributes,
  useId,
} from 'react';
import { type FieldError as FieldErrorType, useFormContext } from 'react-hook-form';
import formStyles from '~/styles/form.module.css';
import getWithDot from '~/helpers/getWithDot';
import useTranslation from '~/hooks/useTranslation';
import FormLabel from './FormLabel';
import FieldError from './FieldError';

type Props = {
  label: string;
  fieldType?: string;
  rows?: number;
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  affix?: string;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  visuallyHiddenLabel?: boolean;
  selectOnFocus?: boolean;
  registerField: string;
};

function TextField({
  label,
  fieldType = 'text',
  rows,
  placeholder,
  autoComplete,
  min,
  max,
  affix,
  required = false,
  visuallyHiddenLabel = false,
  selectOnFocus = false,
  registerField,
}: Props) {
  const fieldId = useId();
  const translate = useTranslation();
  const { register, formState: { errors } } = useFormContext();

  const errorForField = getWithDot<FieldErrorType>(registerField, errors);

  return (
    <div>
      <FormLabel
        htmlFor={fieldId}
        required={required}
        visuallyHidden={visuallyHiddenLabel}
      >
        {label}
      </FormLabel>

      {
        rows
          ? (
            <textarea
              id={fieldId}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register(registerField, {
                required,
                minLength: min,
                maxLength: max,
              })}
              className={formStyles.formControl}
              rows={rows}
              placeholder={placeholder}
              autoComplete={autoComplete}
              aria-invalid={errorForField ? 'true' : 'false'}
            />
          )
          : (
            <div className={formStyles.formControlWrapper}>
              <input
                id={fieldId}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register(registerField, {
                  required,
                  minLength: fieldType !== 'number' ? min : undefined,
                  maxLength: fieldType !== 'number' ? max : undefined,
                  min: fieldType === 'number' ? min : undefined,
                  max: fieldType === 'number' ? max : undefined,
                  pattern: fieldType === 'email'
                    ? {
                      // Source: https://stackoverflow.com/a/50343015
                      value: /^[^@]+@[^@]+\.[^@]+$/,
                      message: translate('form.validation.email'),
                    }
                    : undefined,
                })}
                type={fieldType}
                className={formStyles.formControl}
                placeholder={placeholder}
                min={min}
                max={max}
                autoComplete={autoComplete}
                onFocus={(e) => {
                  if (selectOnFocus) {
                    e.target.select();
                  }
                }}
                aria-invalid={errorForField ? 'true' : 'false'}
              />

              {affix && (
                <div className={formStyles.formControlAffix}>
                  {affix}
                </div>
              )}
            </div>
          )
      }

      {errorForField && (
        <FieldError error={errorForField} />
      )}
    </div>
  );
}

export default TextField;
